import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { StorageService } from './storage.service';
import { StoragePersistKeyEnum } from '../models/enums/storage-persist-key.enum';

@Injectable()
export class AuthenticationService {
  authenticatedChanged = new BehaviorSubject(false);

  constructor(
    private http: HttpClient,
    private storage: StorageService,
    @Inject('API_URL') private serverUrl: string
  ) {
    this.authenticatedChanged.next(false);
  }

  async authenticate(username: string, password: string) {
    // Check if the current token is ok
    if (this.storage.getData<string>(StoragePersistKeyEnum.AccessToken)) {
      this.authenticatedChanged.next(true);
      return 'OK';
    }

    const body = `grant_type=password&username=${username}&password=${password}`;
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });

    try {
      const response = await firstValueFrom(
        this.http.post<{ access_token?: string; userName?: string }>(
          this.serverUrl + 'token',
          body,
          { headers: headers, withCredentials: true }
        )
      );
      if (response.access_token) {
        this.storage.setData(StoragePersistKeyEnum.AccessToken, response.access_token);
        this.storage.setData(StoragePersistKeyEnum.CurrentUser, response.userName);
        this.authenticatedChanged.next(true);
        return 'OK';
      } else {
        return 'error';
      }
    } catch (e) {
      return e;
    }
  }

  getToken(): string | null {
    const token = this.storage.getData<string>(StoragePersistKeyEnum.AccessToken);
    if (token) return token;
    else {
      this.authenticatedChanged.next(false);
      return null;
    }
  }

  getUser(): string | null {
    return this.storage.getData<string>(StoragePersistKeyEnum.CurrentUser);
  }

  // TODO: This method appears unused - all app code uses RemoteService.getRequest() instead.
  // Verify with integration tests before removing. See PR #18 discussion.
  getRequest(url: string) {
    const token = this.getToken();

    if (token) {
      const headers = new HttpHeaders();
      headers.append('Content-Type', 'application/json');
      headers.append('Accept', 'application/json');
      headers.append('Authorization', 'Bearer ' + token);

      const headerObj = {
        headers: headers,
      };

      return this.http.get<unknown>(url, headerObj);
    } else return null;
  }

  logout(): void {
    this.storage.removeData(StoragePersistKeyEnum.AccessToken);
    this.storage.removeData(StoragePersistKeyEnum.CurrentUser);
    this.authenticatedChanged.next(false);
  }
}
